import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import Analytics from './Analytics';
import AdminPanel from './AdminPanel';
import OwnerDashboard from './OwnerDashboard';
import MasterAdminDashboard from './MasterAdminDashboard';
import AIInsights from './AIInsights';
import BillingDashboard from './BillingDashboard';
import CommunicationPortal from './CommunicationPortal';
import MonitoringDashboard from './MonitoringDashboard';
import DepartmentSettings from './DepartmentSettings';
import { 
  Shield, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Plus, 
  Bell,
  User,
  LogOut,
  Users,
  Settings,
  Search,
  BarChart3,
  Download,
  FileDown,
  UserPlus,
  Crown,
  CreditCard,
  Megaphone,
  Brain,
  Activity,
  Building2,
  Palette,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [department, setDepartment] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  
  const { user, logout, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle navigation state for tab switching
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
      // Clear the state to prevent issues on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    fetchDashboardData();
    fetchDepartmentInfo();
    
    // Listen for department updates from settings
    const handleDepartmentUpdate = (event) => {
      console.log('DEBUG: Department update event received:', event.detail);
      if (event.detail && event.detail.logo_url) {
        console.log('DEBUG: Updating department with new logo URL:', event.detail.logo_url);
      }
      setDepartment(event.detail);
      // Force a fresh fetch to ensure we have the latest data
      setTimeout(() => {
        fetchDepartmentInfo();
      }, 100);
    };
    
    window.addEventListener('departmentUpdated', handleDepartmentUpdate);
    
    return () => {
      window.removeEventListener('departmentUpdated', handleDepartmentUpdate);
    };
  }, []);

  // Debug department state changes
  useEffect(() => {
    console.log('DEBUG: Department state updated:', department);
    if (department?.logo_url) {
      console.log('DEBUG: Current logo URL in state:', department.logo_url);
    }
  }, [department]);
  useEffect(() => {
    if (isOwner && activeTab === 'overview') {
      setActiveTab('owner');
    }
  }, [isOwner]);

  const fetchDepartmentInfo = async () => {
    try {
      const response = await axios.get(`${API}/departments/current`);
      setDepartment(response.data);
      // Check if user is owner
      setIsOwner(response.data.is_owner || false);
    } catch (error) {
      console.error('Error fetching department info:', error);
    }
  };

  // Add effect to refresh when navigating back to dashboard
  useEffect(() => {
    const handleFocus = () => {
      fetchDashboardData();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);



  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching dashboard data for user role:', user?.role);
      
      // Make API calls individually with better error handling
      const requestsPromise = axios.get(`${API}/requests`).catch(err => {
        console.error('Requests API error:', err.response?.data || err.message);
        toast.error(`Failed to load requests: ${err.response?.data?.detail || err.message}`);
        return { data: [] };
      });
      
      const statsPromise = axios.get(`${API}/dashboard/stats`).catch(err => {
        console.error('Stats API error:', err.response?.data || err.message);
        toast.error(`Failed to load dashboard stats: ${err.response?.data?.detail || err.message}`);
        return { data: {} };
      });
      
      const notificationsPromise = axios.get(`${API}/notifications`).catch(err => {
        console.error('Notifications API error:', err.response?.data || err.message);
        console.error('Notifications API URL:', `${API}/notifications`);
        console.error('Full error:', err);
        
        // More specific error message
        const errorMsg = err.code === 'NETWORK_ERROR' || err.message === 'Network Error' 
          ? 'Network connection issue. Please check your internet connection and try refreshing the page.'
          : `Failed to load notifications: ${err.response?.data?.detail || err.message}`;
          
        toast.error(errorMsg);
        return { data: [] };
      });

      // Wait for all promises to resolve
      const [requestsRes, statsRes, notificationsRes] = await Promise.all([
        requestsPromise,
        statsPromise,
        notificationsPromise
      ]);

      // Set data from successful responses
      setRequests(requestsRes.data || []);
      setStats(statsRes.data || {});
      setNotifications(notificationsRes.data || []);
      
      console.log('Dashboard data loaded successfully:', {
        userRole: user?.role,
        requests: requestsRes.data?.length || 0,
        stats: statsRes.data,
        notifications: notificationsRes.data?.length || 0
      });
      
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error(`Dashboard loading failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add manual refresh function
  const handleRefresh = () => {
    setLoading(true);
    fetchDashboardData();
  };

  const exportRequestAsPDF = async (requestId) => {
    try {
      const response = await axios.get(`${API}/export/request/${requestId}/pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `request_${requestId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Request exported as PDF');
    } catch (error) {
      toast.error('Failed to export request');
    }
  };

  const exportAllRequests = async () => {
    try {
      const response = await axios.get(`${API}/export/requests/csv`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'requests_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('All requests exported as CSV');
    } catch (error) {
      toast.error('Failed to export requests');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { variant: 'destructive', label: 'Pending' },
      'assigned': { variant: 'default', label: 'Assigned' },
      'in_progress': { variant: 'secondary', label: 'In Progress' },
      'completed': { variant: 'success', label: 'Completed' },
      'denied': { variant: 'destructive', label: 'Denied' }
    };

    const config = statusConfig[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleRequestClick = (requestId) => {
    navigate(`/request/${requestId}`);
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchDashboardData(); // Refresh notifications
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If user is owner, determine type of ownership
  if (isOwner) {
    // Check if this is Master Owner (platform level)
    if (department?.name === 'ReqTrak Platform' && department?.user_role === 'Master Owner') {
      return <MasterAdminDashboard />;
    } else {
      // Regular department owner
      return <OwnerDashboard />;
    }
  }



  // Determine tab configuration based on user role
  const getTabsList = () => {
    const baseTabs = ['overview', 'requests', 'notifications', 'profile'];
    if (user?.role === 'admin') {
      return [...baseTabs.slice(0, 3), 'admin', 'analytics', baseTabs[3]];
    }
    return baseTabs;
  };

  const tabsConfig = getTabsList();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 py-2">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {department?.logo_url ? (
                  <img 
                    src={department.logo_url.startsWith('http') ? department.logo_url : `${process.env.REACT_APP_BACKEND_URL}${department.logo_url}`}
                    alt="Department Logo"
                    className="h-10 w-auto object-contain department-logo"
                    onLoad={() => console.log('DEBUG: Logo loaded successfully:', department.logo_url)}
                    onError={(e) => console.error('DEBUG: Logo failed to load:', department.logo_url, e)}
                  />
                ) : (
                  <img 
                    src="https://customer-assets.emergentagent.com/job_policerecords-2/artifacts/crx2tbck_RequestTrack%20logo.png" 
                    alt="ReqTrak Logo" 
                    className="h-20 w-20 object-contain"
                  />
                )}
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-semibold text-slate-800">
                  {department?.name || 'Records Portal'}
                </h1>
                <p className="text-sm text-slate-600 capitalize">
                  {isOwner ? 'Owner Dashboard' : 
                   user?.role === 'admin' ? 'Admin Dashboard' : 
                   user?.role === 'staff' ? 'Staff Dashboard' : 
                   'Dashboard'}
                </p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-semibold text-slate-800">
                  {department?.name ? department.name.split(' ')[0] : 'Records'}
                </h1>
                <p className="text-xs text-slate-600 capitalize">
                  {isOwner ? 'Owner Dashboard' : 
                   user?.role === 'admin' ? 'Admin Dashboard' : 
                   user?.role === 'staff' ? 'Staff Dashboard' : 
                   'Dashboard'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <div 
                className="relative cursor-pointer"
                onClick={() => setActiveTab('notifications')}
              >
                <Bell className="w-5 h-5 text-slate-600 hover:text-blue-600 transition-colors" />
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {notifications.filter(n => !n.is_read).length}
                  </span>
                )}
              </div>
              
              <div className="hidden md:flex items-center gap-2 text-slate-700">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">{user?.full_name}</span>
              </div>
              
              <Button 
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-red-600"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline ml-1">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={`grid w-full ${(user?.role === 'admin' || isOwner) ? 'grid-cols-5 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'} gap-1`}>
            {isOwner ? (
              <>
                <TabsTrigger value="owner" className="text-xs md:text-sm">Owner Dashboard</TabsTrigger>
                <TabsTrigger value="billing" className="text-xs md:text-sm">Billing</TabsTrigger>
                <TabsTrigger value="analytics" className="text-xs md:text-sm">Analytics</TabsTrigger>
                <TabsTrigger value="settings" className="text-xs md:text-sm">Settings</TabsTrigger>
                <TabsTrigger value="profile" className="text-xs md:text-sm">Profile</TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="overview" className="text-xs md:text-sm">Overview</TabsTrigger>
                <TabsTrigger value="requests" className="text-xs md:text-sm">Requests</TabsTrigger>
                <TabsTrigger value="notifications" className="text-xs md:text-sm hidden md:flex">Notifications</TabsTrigger>
                {user?.role === 'admin' && (
                  <>
                    <TabsTrigger value="admin" className="text-xs md:text-sm">Admin</TabsTrigger>
                    <TabsTrigger value="analytics" className="text-xs md:text-sm hidden md:flex">Analytics</TabsTrigger>
                    <TabsTrigger value="settings" className="text-xs md:text-sm">Settings</TabsTrigger>
                  </>
                )}
                <TabsTrigger value="profile" className="text-xs md:text-sm">Profile</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Role-based Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              
              {/* Admin gets full system stats */}
              {user?.role === 'admin' && (
                <>
                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 p-3 rounded-full">
                          <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Total Requests</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.total_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-amber-100 p-3 rounded-full">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Pending</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.pending_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-full">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Completed</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.completed_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-purple-100 p-3 rounded-full">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Total Users</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.total_users || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Staff gets workload-focused stats */}
              {user?.role === 'staff' && (
                <>
                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 p-3 rounded-full">
                          <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Assigned to Me</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.assigned_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-full">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">My Completed</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.completed_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-amber-100 p-3 rounded-full">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">In Progress</p>
                          <p className="text-2xl font-bold text-slate-800">{(stats.assigned_requests || 0) - (stats.completed_requests || 0)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-purple-100 p-3 rounded-full">
                          <BarChart3 className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Completion Rate</p>
                          <p className="text-2xl font-bold text-slate-800">
                            {stats.assigned_requests > 0 ? Math.round((stats.completed_requests / stats.assigned_requests) * 100) : 0}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Regular users get their personal stats */}
              {user?.role === 'user' && (
                <>
                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 p-3 rounded-full">
                          <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">My Requests</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.total_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-amber-100 p-3 rounded-full">
                          <Clock className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Pending</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.pending_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-green-100 p-3 rounded-full">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Completed</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.completed_requests || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="glass border-0 shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="bg-purple-100 p-3 rounded-full">
                          <BarChart3 className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Status</p>
                          <p className="text-lg font-bold text-slate-800">
                            {stats.pending_requests > 0 ? 'Active' : 'All Clear'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Quick Actions */}
            <Card className="glass border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and shortcuts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Button 
                    className="h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm sm:text-base"
                    onClick={() => navigate('/request/new')}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    New Request
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-12 text-sm sm:text-base"
                    onClick={() => setActiveTab('requests')}
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search Records
                  </Button>
                  
                  {/* Staff Workspace Link */}
                  {user?.role === 'staff' && (
                    <Button 
                      variant="outline" 
                      className="h-12 border-green-300 text-green-700 hover:bg-green-50 text-sm sm:text-base"
                      onClick={() => navigate('/staff-workspace')}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Staff Workspace</span>
                      <span className="sm:hidden">Staff</span>
                    </Button>
                  )}
                  
                  {user?.role === 'admin' && (
                    <>
                      <Button 
                        variant="outline" 
                        className="h-12 text-sm sm:text-base"
                        onClick={() => setActiveTab('admin')}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Admin Panel</span>
                        <span className="sm:hidden">Admin</span>
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="h-12 border-blue-300 text-blue-700 hover:bg-blue-50 text-sm sm:text-base"
                        onClick={() => setActiveTab('billing')}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Billing
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="h-12 border-purple-300 text-purple-700 hover:bg-purple-50 text-sm sm:text-base"
                        onClick={() => setActiveTab('communications')}
                      >
                        <Megaphone className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Communications</span>
                        <span className="sm:hidden">Comms</span>
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="h-12 border-orange-300 text-orange-700 hover:bg-orange-50 text-sm sm:text-base"
                        onClick={() => setActiveTab('monitoring')}
                      >
                        <Activity className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Monitoring</span>
                        <span className="sm:hidden">Monitor</span>
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="h-12"
                        onClick={exportAllRequests}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Data
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Requests */}
            <Card className="glass border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Requests</CardTitle>
                    <CardDescription>Your latest records requests</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requests.slice(0, 5).map((request) => (
                    <div 
                      key={request.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer transition-colors gap-3"
                      onClick={() => handleRequestClick(request.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-800 truncate">{request.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">
                          {formatDate(request.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getStatusBadge(request.status)}
                        <Badge variant="outline" className="text-xs">{request.request_type.replace('_', ' ')}</Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportRequestAsPDF(request.id);
                            }}
                          >
                            <FileDown className="w-4 h-4" />
                          </Button>
                          
                          {(user?.role === 'admin' || user?.role === 'staff') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-purple-600 hover:text-purple-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('ai-insights');
                                // Store selected request for AI insights
                                sessionStorage.setItem('selectedRequestId', request.id);
                              }}
                            >
                              <Brain className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {requests.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No requests yet</p>
                      <Button 
                        onClick={() => navigate('/request/new')}
                        className="mt-3"
                        variant="outline"
                      >
                        Create your first request
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            <Card className="glass border-0 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Requests</CardTitle>
                  <CardDescription>Manage and track your records requests</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button 
                    onClick={() => navigate('/request/new')}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Request
                  </Button>
                  {user?.role === 'admin' && (
                    <Button 
                      variant="outline"
                      onClick={exportAllRequests}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requests.length > 0 ? (
                    requests.map((request) => (
                      <div 
                        key={request.id}
                        className="p-6 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer transition-all hover:shadow-md"
                        onClick={() => handleRequestClick(request.id)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">{request.title}</h3>
                            <p className="text-slate-600 mb-2">{request.description.substring(0, 150)}...</p>
                            <p className="text-sm text-slate-500">
                              Submitted on {formatDate(request.created_at)}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(request.status)}
                            <Badge variant="outline">{request.request_type.replace('_', ' ')}</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportRequestAsPDF(request.id);
                              }}
                            >
                              <FileDown className="w-4 h-4" />
                            </Button>
                            
                            {(user?.role === 'admin' || user?.role === 'staff') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:text-purple-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab('ai-insights');
                                  // Store selected request for AI insights
                                  sessionStorage.setItem('selectedRequestId', request.id);
                                }}
                              >
                                <Brain className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span>Priority: {request.priority}</span>
                          {request.assigned_staff_id && (
                            <span>• Assigned to staff</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No requests found</p>
                      <p className="text-sm">
                        {loading ? 'Loading requests...' : 'Check console for API errors or create a new request to get started'}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => fetchDashboardData()}
                      >
                        Retry Loading
                      </Button>
                    </div>
                  )}
                  
                  {requests.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <h3 className="text-lg font-medium mb-2">No requests found</h3>
                      <p className="mb-4">Create your first records request to get started</p>
                      <Button 
                        onClick={() => navigate('/request/new')}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Request
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6">
            <Card className="glass border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Notifications</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fetchDashboardData()}
                    disabled={loading}
                  >
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </CardTitle>
                <CardDescription>Stay updated on your requests and system updates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                        notification.is_read 
                          ? 'border-slate-200 bg-slate-50' 
                          : 'border-blue-200 bg-blue-50'
                      }`}
                      onClick={() => markNotificationRead(notification.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-800">{notification.title}</h4>
                          <p className="text-slate-600 mt-1">{notification.message}</p>
                          <p className="text-sm text-slate-500 mt-2">
                            {formatDate(notification.created_at)}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {notifications.length === 0 && !loading && (
                    <div className="text-center py-8 text-slate-500">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No notifications</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => fetchDashboardData()}
                      >
                        Check for notifications
                      </Button>
                    </div>
                  )}
                  
                  {loading && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                      <p className="text-slate-500">Loading notifications...</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {(user?.role === 'admin' || isOwner) && (
            <>
              {isOwner && (
                <TabsContent value="owner" className="space-y-6">
                  {/* Owner Dashboard Content - Consolidated */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Department Overview */}
                    <Card className="glass border-0 shadow-lg">
                      <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          Department Overview
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="font-medium">{department?.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Subdomain</p>
                          <p className="font-medium">{department?.subdomain}.reqtrak.com</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Status</p>
                          <Badge className="bg-green-100 text-green-800">
                            {department?.subscription_status || 'Active'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Department Stats */}
                    <Card className="glass border-0 shadow-lg">
                      <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-green-600" />
                          Department Stats
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-600">Total Requests</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.total_requests || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Users</p>
                          <p className="text-2xl font-bold text-slate-800">{stats.total_users || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Active Staff</p>
                          <p className="text-2xl font-bold text-slate-800">{department?.staff_count || 0}</p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card className="glass border-0 shadow-lg">
                      <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2">
                          <Settings className="w-5 h-5 text-purple-600" />
                          Quick Actions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button 
                          onClick={() => setActiveTab('settings')}
                          variant="outline" 
                          className="w-full justify-start"
                        >
                          <Palette className="w-4 h-4 mr-2" />
                          Department Settings
                        </Button>
                        <Button 
                          onClick={() => setActiveTab('billing')}
                          variant="outline" 
                          className="w-full justify-start"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Billing & Subscription
                        </Button>
                        <Button 
                          onClick={() => setActiveTab('analytics')}
                          variant="outline" 
                          className="w-full justify-start"
                        >
                          <BarChart3 className="w-4 h-4 mr-2" />
                          View Analytics
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Recent Activity */}
                  <Card className="glass border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle>Recent Department Activity</CardTitle>
                      <CardDescription>Latest requests and user activity</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {requests.slice(0, 5).map((request) => (
                          <div key={request.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <div>
                                <p className="font-medium text-sm">{request.title}</p>
                                <p className="text-xs text-slate-600">
                                  {new Date(request.created_at).toLocaleDateString()} • {request.user_name}
                                </p>
                              </div>
                            </div>
                            <Badge variant={request.status === 'completed' ? 'default' : 'secondary'}>
                              {request.status}
                            </Badge>
                          </div>
                        ))}
                        
                        {requests.length === 0 && (
                          <div className="text-center py-8">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">No recent requests</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
              
              {user?.role === 'admin' && (
                <TabsContent value="admin" className="space-y-6">
                  <AdminPanel />
                </TabsContent>
              )}
              
              <TabsContent value="billing" className="space-y-6">
                <BillingDashboard />
              </TabsContent>
              
              <TabsContent value="communications" className="space-y-6">
                <CommunicationPortal />
              </TabsContent>
              
              <TabsContent value="monitoring" className="space-y-6">
                <MonitoringDashboard />
              </TabsContent>
              
              <TabsContent value="ai-insights" className="space-y-6">
                <AIInsights 
                  requestId={sessionStorage.getItem('selectedRequestId')} 
                  requestData={requests.find(r => r.id === sessionStorage.getItem('selectedRequestId'))}
                />
              </TabsContent>
              
              <TabsContent value="analytics" className="space-y-6">
                <Analytics />
              </TabsContent>
              
              <TabsContent value="settings" className="space-y-6">
                <DepartmentSettings />
              </TabsContent>
            </>
          )}

          <TabsContent value="profile" className="space-y-6">
            <Card className="glass border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Your account details and settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center">
                    <span className="text-white text-xl font-semibold">
                      {user?.full_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">{user?.full_name}</h3>
                    <p className="text-slate-600">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="capitalize">{user?.role}</Badge>
                      {isOwner && <Badge className="bg-amber-100 text-amber-800">Owner</Badge>}
                    </div>
                  </div>
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Account Type</Label>
                    <p className="mt-1 text-slate-800 capitalize">{user?.role}</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Member Since</Label>
                    <p className="mt-1 text-slate-800">{formatDate(user?.created_at)}</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Status</Label>
                    <p className="mt-1 text-green-600">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;